<template>
  <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
    <div>
      <h3 class="text-slate font-semibold leading-6 text-gray-900">Send Work Order</h3>

      <div class="grid grid-cols-2 gap-6 mt-4">
        <!-- email details -->
        <div>
          <form @submit.stop.prevent="form.handleSubmit()">
            <form.Field
              name="from"
              :validators="{
                onChange: yup.string().required('From is required').email('Invalid email address')
              }"
            >
              <template #default="{ field }">
                <BaseInput
                  v-model="(field.state.value as string)"
                  label="From"
                  placeholder="Enter email address"
                  type="email"
                  :name="field.name"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @update:model-value="(value) => field.handleChange(value)"
                />
              </template>
            </form.Field>

            <form.Field
              name="to"
              :validators="{
                onChange: yup.array().of(yup.string().email('Invalid email address')).min(1, 'Email for is required')
              }"
            >
              <template #default="{ field }">
                <BaseTagsInput
                  v-model="(field.state.value as string[])"
                  label="Email for"
                  placeholder="add email address"
                  type="email"
                  description="Enter new emails separated by space, comma or semicolon"
                  :name="field.name"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @update:model-value="(value) => field.handleChange(value)"
                />
              </template>
            </form.Field>

            <form.Field
              name="message.subject"
              :validators="{
                onBlur: yup.string().required('Subject is required')
              }"
            >
              <template #default="{ field }">
                <BaseInput
                  v-model="(field.state.value as string)"
                  label="Subject"
                  :name="field.name"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  placeholder="Subject"
                  @blur="field.handleBlur"
                  @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
                />
              </template>
            </form.Field>
          </form>

          <div class="preview-email" v-html="previewHTML" />

          <div class="mt-8">
            <!-- copy share link -->
            <div class="mb-3">
              <span class="font-bold">Share Work Order</span>
            </div>
            <div class="flex items-start gap-2">
              <BaseButton
                text="Copy link"
                :icon-class="isCopying ? 'i-heroicons-check' : 'i-heroicons-clipboard'"
                @click="copyToClipboard"
              />
              <div class="flex-1">
                <BaseInput
                  v-model="shareLink"
                  readonly
                  placeholder="Share link"
                />
              </div>

            </div>
          </div>
        </div>

        <!-- pdf viewer -->
        <div>
          <embed
            v-if="pdfReady"
            class="pdfobject"
            type="application/pdf"
            title="Embedded PDF"
            :src="pdfLink"
            style="width: 100%; min-height: 100%;"
          >

          <div v-else>
            <div class="flex flex-col items-center justify-center h-full">
              <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
              <p class="text-sm text-center text-slate-500 pt-4">
                Generating PDF...
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="border-t border-gray-200 px-4 sm:px-6" />

  <form.Subscribe>
    <template #default="{ isSubmitting, canSubmit }">
      <div class="bg-white py-3 flex px-6 justify-between gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isSubmitting"
          :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
          @click="emit('close')"
        />

        <BaseButton
          variant="primary"
          :disabled="!canSubmit || isSubmitting || !pdfReady"
          :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting || !pdfReady }"
          @click="form.handleSubmit()"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Send and close</span>
            <span
              v-if="isSubmitting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </form.Subscribe>
</template>

<script lang="ts" setup>
import { Email } from '@/models/email'
import { Subcontractor } from '@/models/subcontractor'
import { WorkOrder } from '@/models/workOrder'
import { useForm } from '@tanstack/vue-form'
import { computed, PropType, ref } from 'vue'

import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTagsInput from '@/components/base/BaseTagsInput.vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation } from '@tanstack/vue-query'
import { httpsCallable } from 'firebase/functions'
import FirebaseService from '@/services/FirebaseService'
import BaseButton from '@/components/base/BaseButton.vue'
import { useRouter } from 'vue-router'

const props = defineProps({
  email: {
    type: Object as PropType<Email>,
    required: true
  },
  subcontractor: {
    type: Object as PropType<Subcontractor>,
    required: true
  },
  pdfLink: {
    type: String,
    required: true
  },
  workOrder: {
    type: Object as PropType<WorkOrder>,
    required: true
  },
  pdfReady: {
    type: Boolean,
    required: true
  }
})

const { notify } = useNotification()
const emit = defineEmits(['close'])

const viewWorkOrderLink = useRouter().resolve({
  name: 'ViewWorkOrder',
  params: { id: props.workOrder.id }
})
const shareLink = computed(() => `${window.location.origin}${viewWorkOrderLink.href}`)
const isCopying = ref(false)

const copyToClipboard = async () => {
  try {
    isCopying.value = true
    await navigator.clipboard.writeText(shareLink.value)
  } catch (error) {
    notify({
      type: 'error',
      title: 'Error.',
      text: 'Error copying link.'
    })
  }
  setTimeout(() => {
    isCopying.value = false
  }, 1000)
}

const form = useForm({
  defaultValues: props.email,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    await sendEmail()
  }
})

const previewHTML = computed(() => props.email.message.html)


const { mutateAsync: sendEmail } = useMutation({
  mutationFn: async () => {
    const functions = await FirebaseService.functions()
    const send = httpsCallable(functions, 'email-sendV2')
    await send(props.email)
  },
  onSuccess: () => {
    emit('close')
    notify({
      type: 'success',
      title: 'Email sucessfully sent!'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error.',
      text: error.message || 'Error sending email.'
    })
  }
})
</script>

<style scoped>
.preview-email {
  border: 1px solid #e9ecef;
  padding: 1rem;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
  max-height: 380px;
  overflow-y: scroll;
}
</style>
